import React, { useState, useEffect } from 'react';
import { Sword, ShieldBan, Sun, Moon, Sparkles } from "lucide-react";

interface MatchScoreVisualizerProps {
  player1Name: string | React.JSX.Element;
  player2Name: string | React.JSX.Element;
  winHistory: number[]; // 正の数=下側(player2)の勝ち、負の数=上側(player1)の勝ち、絶対値1~5で勝ち方を表す
}

// 勝ち方に応じたマークを返す
const WinMark: React.FC<{ type: number; color: string }> = ({ type, color }) => {
  const absType = Math.abs(type);

  switch (absType) {
    case 2:
      return <ShieldBan size={24} color='white'></ShieldBan>
    case 3:
      return <Sun size={24} color='white'></Sun>;
    case 4:
      return <Moon size={24} color='white'></Moon>;
    case 5:
      return <Sparkles size={24} color='white'></Sparkles>;
    case 1:
    default:
      return <Sword size={24} color='white'></Sword>;
  }
};

export const ScoreBoard: React.FC<MatchScoreVisualizerProps> = ({
  player1Name,
  player2Name,
  winHistory,
}) => {
  const [visibleCount, setVisibleCount] = useState(0);

  // アニメーション効果
  useEffect(() => {
    setVisibleCount(0);
    const timer = setInterval(() => {
      setVisibleCount(prev => {
        if (prev >= winHistory.length) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 150);

    return () => clearInterval(timer);
  }, [winHistory]);

  // 最終スコアを計算
  const player1Score = winHistory.slice(0, visibleCount).filter(w => w < 0).length;
  const player2Score = winHistory.slice(0, visibleCount).filter(w => w > 0).length;

  const player1Color = '#3b82f6'; // blue
  const player2Color = '#ef4444'; // red

  return (

    <div className="bg-white/30 rounded-lg shadow-lg overflow-x-auto max-w-full">
      {/* Grid Container */}
      <div
        className="relative"
        style={{
          display: 'grid',
          gridTemplateColumns: `120px repeat(${winHistory.length}, 1fr) 80px`,
          gridTemplateRows: 'auto auto'
        }}
      >

        {/* プレイヤー1の行 */}
        {/* 名前列 */}
        <div className="px-4 py-4 font-bold text-lg bg-red-50/30 border-r-2 border-b-2 border-white flex items-center">
          <div className="truncate">{player1Name}</div>
        </div>

        {/* 各ゲームの結果 */}
        {winHistory.map((result, index) => (
          <div
            key={`p1-${index}`}
            className="px-2 py-4 flex items-center justify-center border-r border-b-2 border-white transition-opacity duration-300 relative z-20"
            style={{ opacity: index < visibleCount ? 1 : 0 }}
          >
            {result < 0 && (
              <div
                className="transition-transform duration-300"
                style={{
                  transform: index < visibleCount ? 'scale(1)' : 'scale(0)',
                }}
              >
                <WinMark type={result} color={player1Color} />
              </div>
            )}
          </div>
        ))}

        {/* 最終スコア列 */}
        <div className="px-4 py-4 font-bold text-2xl text-white text-center bg-red-50/30 border-l-2 border-b-2 border-white flex items-center justify-center">
          {player1Score}
        </div>

        {/* プレイヤー2の行 */}
        {/* 名前列 */}
        <div className="px-4 py-4 font-bold text-lg bg-blue-50/30 border-r-2 border-white flex items-center">
          <div className="truncate">{player2Name}</div>
        </div>

        {/* 各ゲームの結果 */}
        {winHistory.map((result, index) => (
          <div
            key={`p2-${index}`}
            className="px-2 py-4 flex items-center justify-center border-r border-white transition-opacity duration-300 relative z-20"
            style={{ opacity: index < visibleCount ? 1 : 0 }}
          >
            {result > 0 && (
              <div
                className="transition-transform duration-300"
                style={{
                  transform: index < visibleCount ? 'scale(1)' : 'scale(0)',
                }}
              >
                <WinMark type={result} color={player2Color} />
              </div>
            )}
          </div>
        ))}

        {/* 最終スコア列 */}
        <div className="px-4 py-4 font-bold text-2xl text-white text-center bg-blue-50/30 border-l-2 border-white flex items-center justify-center">
          {player2Score}
        </div>
      </div>
    </div>
  );
};
